import React, {useState} from 'react';
import b_ from 'b_';

import {BoxType, SurveysGoal, SurveysParam} from 'client/common/types';

import {Spin, Button} from 'client/common/components/lego';
import PaymentNotPossiblePopup from 'client/common/components/payment-not-possible-popup';
import CountryModal from 'client/common/components/country-modal';

import {sendGoal, sendParams} from 'client/common/utils/metrika';
import I18N from 'assets/i18n/brandlift-project';

import {useBoxType} from 'client/common/hooks/box';
import {useModal} from 'client/common/hooks/modal';
import {usePaymentStatus, usePrice} from 'client/common/hooks/payment';
import {usePublishRevision} from 'client/common/hooks/revision';
import {useSurvey} from 'client/common/hooks/survey';
import {useIsAvailableUserCurrency, useIsCreativeAssessor, useIsSuperuser, useUserCurrency} from 'client/common/hooks/user';
import {useAudienceRequest} from 'client/common/hooks/audience';

import {tld} from 'client/common/utils/tld';
import {getIsFree} from 'client/common/utils/price';
import {useSurveySlug} from 'client/common/contexts/slug';

import {SurveyState} from 'client/common/store/reducers/survey/types';

import {checkPayed} from 'client/common/utils/payment';

import {PublicationButtonProps} from './types';

import Publication from './publication';

import './publication.css';

const b = b_.with('publication');

const i18n = I18N.common.publication;

interface LabelProps {
    boxType: BoxType | 'PRO' | undefined;
    state: SurveyState;
    isPayed: boolean;
    canLaunch: boolean;
    cost: string;
}

function getLabel(props: LabelProps): React.ReactNode | string {
    const {boxType, state, isPayed, canLaunch, cost} = props;

    if (state === SurveyState.Draft || state === SurveyState.Moderation) {
        if (boxType === 'creative' || boxType === 'bordomatik') {
            return (
                <span>
                    {I18N.creatives.launch}
                    <span className={b('cost')}>{cost}</span>
                </span>
            );
        }

        if (state === SurveyState.Moderation && !isPayed && !canLaunch) {
            return i18n.pay;
        }

        return i18n.launch;
    }

    return i18n.publishChanges;
}

// eslint-disable-next-line max-statements
export default function PublicationButton(props: PublicationButtonProps) {
    const {
        onClick,
        onPublish,
        onModeration,
        withPreview,
        label,
        isNewView,
        cost = '',
        size = 'm',
        ...rest
    } = props;

    const slug = useSurveySlug();

    const survey = useSurvey(slug);
    const surveyType = useBoxType(slug) ?? 'PRO';
    const paymentStatus = usePaymentStatus(slug);
    const isSuperuser = useIsSuperuser();
    const isCreativeAssessor = useIsCreativeAssessor();
    const audienceRequest = useAudienceRequest(slug);
    const price = usePrice(slug);

    const userCurrency = useUserCurrency();
    const isAvailableCurrency = useIsAvailableUserCurrency();

    const [isPublishPending, setIsPublishPending] = useState(false);

    const publishRevision = usePublishRevision();

    const [showModal, hideModal] = useModal();

    if (
        survey.status !== 'success' ||
        paymentStatus.status !== 'success' ||
        audienceRequest.status !== 'success' ||
        price.status !== 'success'
    ) {
        return <Spin size='xs' view='default' progress />;
    }

    if ([SurveyState.Completed, SurveyState.Closed].includes(survey.data.state)) {
        return null;
    }

    const {state} = survey.data;
    const isFree = getIsFree(price.data);

    const getView = () => {
        const shouldDisplayAction = [SurveyState.Draft, SurveyState.Moderation].includes(
            survey.data.state
        );

        if (shouldDisplayAction) {
            return 'action';
        }

        return 'default';
    };

    const handleCloseModeration = () => {
        hideModal();

        if (!isSuperuser && onModeration) {
            onModeration();
        }
    };

    const publish = async () => {
        setIsPublishPending(true);
        if (
            !isFree &&
            (survey.data.state === SurveyState.Draft ||
                (survey.data.state === SurveyState.Moderation && !isSuperuser))
        ) {
            sendParams([SurveysParam.LaunchedSurveyType, surveyType]);
            sendGoal(SurveysGoal.ClickLaunch);

            setIsPublishPending(false);

            showModal(<Publication onClose={handleCloseModeration} withPreview={withPreview} />, {
                withCross: true
            });

            return;
        }

        await publishRevision();

        setIsPublishPending(false);

        if (onPublish) {
            onPublish();
        }
    };

    const handleClick = async () => {
        if (!userCurrency) {
            showModal(
                <CountryModal
                    onDone={() => {
                        location.reload();
                    }}
                    tld={tld}
                />,
                {hideByOutsideClick: false}
            );

            return;
        }
        if (!isAvailableCurrency) {
            showModal(<PaymentNotPossiblePopup onClose={hideModal}/>, {withCross: true});

            return;
        }
        if (onClick) {
            onClick(publish); // передаем коллбэк наружу, для отложенного запуска публикации
        } else {
            await publish();
        }
    };

    return (
        <Button
            onClick={handleClick}
            progress={isPublishPending}
            size={size}
            view={isNewView ? 'dl-action' : getView()}
            {...rest}
        >
            {label ??
                getLabel({
                    state,
                    isPayed: checkPayed(paymentStatus.data),
                    canLaunch: isSuperuser || isCreativeAssessor,
                    cost,
                    boxType: surveyType
                })}
        </Button>
    );
}
